import { faBarsFilter } from '@fortawesome/pro-solid-svg-icons'
import { IconButton, MenuButton } from 'components/buttons'
import { useTranslation } from 'react-i18next'
import { twJoin } from 'tailwind-merge'
import { SearchQuery } from '../search-query'
import { MenuItem } from './MenuItem'

export interface EmployeeFilterType {
  name: string
  // Will fold in when salaraied/hourly are added
  // type: ('salaried' | 'hourly' | 'unmanaged' | 'admin-managed')[]
  type: ('unmanaged' | 'adminManaged')[]
}

const allItems: EmployeeFilterType['type'] = ['unmanaged', 'adminManaged']

interface Props {
  columnNameTranslationKey?: TranslationKey
  onChange: (filter: EmployeeFilterType) => void
  value: EmployeeFilterType
}

export const EmployeeFilter = ({
  columnNameTranslationKey = 'common.employeeName' as const,
  onChange,
  value: employeeFilter,
}: Props) => {
  const { t } = useTranslation()
  // An empty array means "all types selected", so there is never a partial
  // selection to enumerate. A non-empty array is always a proper subset.
  const allSelected = employeeFilter.type.length === 0
  const someSelected = employeeFilter.type.length > 0

  // Expand the "all" shorthand before toggling an individual type off, then
  // collapse a full selection back to [] so "all" is stored consistently.
  const effectiveTypes = allSelected ? allItems : employeeFilter.type
  const normalize = (types: EmployeeFilterType['type']) =>
    allItems.every((type) => types.includes(type)) ? [] : types

  return (
    <div className="relative">
      <SearchQuery
        columnNameTranslationKey={columnNameTranslationKey}
        onChange={(value) => onChange({ ...employeeFilter, name: value ?? '' })}
        query={employeeFilter.name}
      />
      <MenuButton
        button={({ clickHandler }) => (
          <IconButton
            className={twJoin(
              someSelected &&
                !allSelected &&
                'text-primary-600 bg-primary-100 hover:bg-primary-200',
            )}
            icon={faBarsFilter}
            onClick={clickHandler}
          />
        )}
        className="absolute top-1 right-1"
        closeMenuOnItemClick={false}
        menuItems={[
          {
            as: 'div',
            className: 'h-11 px-6 hover:bg-neutral-100',
            content: (
              <MenuItem
                label="All"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() =>
                  onChange({
                    ...employeeFilter,
                    type: [],
                  })
                }
              />
            ),
          },
          ...allItems.map((type) => ({
            as: 'div' as const,
            className: 'h-11 px-6 hover:bg-neutral-100',
            content: (
              <MenuItem
                key={type}
                checked={allSelected || employeeFilter.type.includes(type)}
                onChange={(checked) =>
                  onChange({
                    ...employeeFilter,
                    type: normalize(
                      checked
                        ? [...effectiveTypes, type]
                        : effectiveTypes.filter((t) => t !== type),
                    ),
                  })
                }
                label={t(`common.${type}`)}
              />
            ),
          })),
        ]}
        preventEventPropagation={true}
      />
    </div>
  )
}
